import { apiClient } from './client';
import type {
  AuthResponse,
  LoginCredentials,
  RefreshResponse,
  User,
} from '../../types';

interface MeResponse {
  user: User;
}

export interface ImpersonatableUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export interface ImpersonateResponse extends AuthResponse {
  impersonation: {
    adminUserId: string;
    adminEmail: string;
    adminName: string;
  };
}

export interface ImpersonationLogEntry {
  id: string;
  admin_user_id: string;
  impersonated_user_id: string;
  impersonated_persona: string;
  started_at: string;
  ended_at: string | null;
  ip_address: string | null;
  admin_name: string;
  admin_email: string;
  impersonated_name: string;
  impersonated_email: string;
  created_at: string;
}

export interface ImpersonationLogResponse {
  entries: ImpersonationLogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SessionActivity {
  id: string;
  source: string;
  action_type: string;
  description: string;
  user_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export const authService = {
  login: (credentials: LoginCredentials): Promise<AuthResponse> =>
    apiClient.post<AuthResponse>('/api/auth/login', credentials),

  logout: (refreshToken: string): Promise<void> =>
    apiClient.post('/api/auth/logout', { refreshToken }),

  logoutAll: (): Promise<{ sessionsRevoked: number }> =>
    apiClient.post('/api/auth/logout-all'),

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<MeResponse>('/api/auth/me');
    return response.user;
  },

  refreshToken: (refreshToken: string): Promise<RefreshResponse> =>
    apiClient.post<RefreshResponse>('/api/auth/refresh', { refreshToken }),

  validateToken: (): Promise<{
    valid: boolean;
    userId: string;
    email: string;
    role: string;
  }> => apiClient.get('/api/auth/validate'),

  validateInvitation: (token: string): Promise<{
    valid: boolean;
    email: string;
    firstName: string;
    lastName: string;
  }> => apiClient.get(`/api/auth/invitation/${token}`),

  acceptInvitation: (token: string, password: string): Promise<AuthResponse & { message: string }> =>
    apiClient.post('/api/auth/invitation/accept', { token, password }),

  // Password reset methods
  forgotPassword: (email: string): Promise<{ message: string }> =>
    apiClient.post('/api/auth/forgot-password', { email }),

  validateResetToken: (token: string): Promise<{ valid: boolean; email: string }> =>
    apiClient.get(`/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`),

  resetPassword: (token: string, password: string): Promise<{ message: string }> =>
    apiClient.post('/api/auth/reset-password', { token, password }),

  adminResetPassword: (userId: string): Promise<{ message: string }> =>
    apiClient.post('/api/auth/admin/reset-password', { userId }),

  // Magic link methods
  requestMagicLink: (email: string): Promise<{ message: string }> =>
    apiClient.post('/api/auth/magic-link/request', { email }),

  verifyMagicLink: (token: string): Promise<AuthResponse & {
    isFirstLogin: boolean;
  }> => apiClient.post('/api/auth/magic-link/verify', { token }),

  // Impersonation methods
  impersonate: (targetUserId: string): Promise<ImpersonateResponse> =>
    apiClient.post<ImpersonateResponse>('/api/auth/impersonate', { targetUserId }),

  // MED-1383: originalRefreshToken is the admin's own token from before the
  // impersonation. The API revokes it, because it returns a new admin pair.
  endImpersonate: (refreshToken?: string, originalRefreshToken?: string): Promise<AuthResponse> =>
    apiClient.post<AuthResponse>('/api/auth/end-impersonate', {
      refreshToken,
      originalRefreshToken,
    }),

  listUsersByPersona: (persona: string): Promise<{ users: ImpersonatableUser[] }> =>
    apiClient.get<{ users: ImpersonatableUser[] }>(`/api/auth/users-by-persona/${persona}`),

  getImpersonationLog: (page = 1, limit = 25): Promise<ImpersonationLogResponse> =>
    apiClient.get<ImpersonationLogResponse>(
      `/api/auth/impersonation-log?page=${page}&limit=${limit}`,
    ),

  getSessionActivities: (sessionId: string): Promise<{ activities: SessionActivity[] }> =>
    apiClient.get<{ activities: SessionActivity[] }>(
      `/api/auth/impersonation-log/${sessionId}/activities`,
    ),
};

export default authService;
